import { observer } from 'mobx-react-lite'
import { useCallback, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { Box, Group, SegmentedControl, Stack } from '@mantine/core'
import { AppTextarea } from '../../app/common/ui'
import { useDropzone } from 'react-dropzone'
import { toast } from 'react-toastify'
import { IoArrowBack, IoCloudUploadOutline, IoCloseCircle } from 'react-icons/io5'
import agent from '../../app/api/agent'
import type { FirearmModel } from '../../app/models/firearmModel'
import type { CacheVisibility } from '../../app/models/cache'
import { AppButton, AppCard, AppInput, AppText, EmptyState } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import { canCreateCache, MIN_AMMO_SCORE_TO_CREATE_CACHE } from '../../app/common/constants/reputation'
import { CACHE_VISIBILITY_OPTIONS, CacheCover, resolveCacheCover, visibilityMeta } from './cacheUi'

const PAGE_MAX = 620

export default observer(function CreateCachePage () {
  const { userStore } = useStore()
  const history = useHistory()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [visibility, setVisibility] = useState<CacheVisibility>('Public')
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [modelSearch, setModelSearch] = useState('')
  const [modelResults, setModelResults] = useState<FirearmModel[]>([])
  const [selectedModel, setSelectedModel] = useState<FirearmModel | null>(null)
  const [modelSearching, setModelSearching] = useState(false)
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  const onDrop = useCallback(async (files: File[]) => {
    const file = files[0]
    if (!file) return
    setUploading(true)
    try {
      const result = await agent.Caches.uploadCover(file) as { url?: string } | undefined
      setCoverImageUrl(result?.url ?? null)
    } catch {
      toast.error('Failed to upload cover image')
    } finally {
      setUploading(false)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg,image/jpg,image/png,image/gif,image/webp',
    maxFiles: 1,
    disabled: uploading
  })

  if (!userStore.isLoggedIn) {
    return (
      <Box sx={{ padding: 24, maxWidth: PAGE_MAX, margin: '0 auto' }}>
        <EmptyState
          title="Log in to create a cache"
          action={{ label: 'Log in', onClick: () => history.push('/login') }}
        />
      </Box>
    )
  }

  if (!canCreateCache(userStore.user)) {
    return (
      <Box sx={{ padding: 24, maxWidth: PAGE_MAX, margin: '0 auto' }}>
        <EmptyState
          title="Not enough AmmoScore yet"
          message={`You need an AmmoScore of ${MIN_AMMO_SCORE_TO_CREATE_CACHE} to start a cache. Keep contributing to earn it.`}
          action={{ label: 'Back to caches', onClick: () => history.push('/caches') }}
        />
      </Box>
    )
  }

  const handleModelSearchChange = (value: string) => {
    setModelSearch(value)
    setSelectedModel(null)
    if (searchDebounce.current) clearTimeout(searchDebounce.current)
    if (!value.trim()) {
      setModelResults([])
      return
    }
    searchDebounce.current = setTimeout(() => {
      setModelSearching(true)
      agent.FirearmModels.list({ make: value.trim(), pageSize: 10 })
        .then((r) => setModelResults(r.items ?? []))
        .catch(() => setModelResults([]))
        .finally(() => setModelSearching(false))
    }, 350)
  }

  const selectModel = (m: FirearmModel) => {
    setSelectedModel(m)
    setModelSearch(`${m.make} ${m.model}`)
    setModelResults([])
  }

  const clearModel = () => {
    setSelectedModel(null)
    setModelSearch('')
    setModelResults([])
  }

  const submit = async () => {
    if (!name.trim()) {
      toast.error('Name is required')
      return
    }
    setSubmitting(true)
    try {
      const cache = await agent.Caches.create({
        name: name.trim(),
        description: description.trim() || undefined,
        coverImageUrl: coverImageUrl ?? undefined,
        firearmModelId: selectedModel?.id,
        visibility
      })
      history.push(`/caches/${cache.id}`)
    } finally {
      setSubmitting(false)
    }
  }

  // Preview cover: uploaded wins, else the selected registry model's image.
  const previewCover = resolveCacheCover({
    coverImageUrl,
    firearmModelCoverImageUrl: selectedModel?.coverImageUrl ?? null
  })

  return (
    <Box sx={{ padding: '16px 24px 48px', maxWidth: PAGE_MAX, margin: '0 auto' }}>
      <AppButton component={Link} to="/caches" variant="subtle" size="xs" mb="sm" leftIcon={<IoArrowBack size={13} />}>Caches</AppButton>
      <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 24, letterSpacing: 0.5 }} mb="lg">
        NEW CACHE
      </AppText>

      <AppCard padding="lg">
        <Stack spacing="lg">
          <AppInput
            label="Name"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.currentTarget.value)}
            required
            maxLength={200}
            placeholder="e.g. Colt Single-Action Army Fan Club"
            description={`${name.length}/200`}
          />

          <AppTextarea
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            minRows={3}
            autosize
            maxRows={8}
            maxLength={2000}
            placeholder="What's this community about?"
            description={`${description.length}/2000`}
          />

          {/* Cover */}
          <Box>
            <AppText size="sm" weight={600} mb={6}>Cover image <AppText span size="xs" color="dimmed">(optional)</AppText></AppText>
            <CacheCover url={previewCover} name={name || 'New cache'} aspectRatio="16 / 9" radius={10} loading={uploading} />
            {!coverImageUrl && selectedModel?.coverImageUrl && (
              <AppText size="xs" color="dimmed" mt={6}>Using the linked model&apos;s image. Upload one to override.</AppText>
            )}
            <Group spacing="xs" mt="sm">
              <Box
                {...getRootProps()}
                sx={{
                  flex: 1,
                  border: `1px dashed ${isDragActive ? 'var(--gs-accent)' : 'var(--gs-border)'}`,
                  borderRadius: 8,
                  padding: '10px 12px',
                  cursor: uploading ? 'wait' : 'pointer',
                  textAlign: 'center',
                  '&:hover': { borderColor: 'var(--gs-accent)' }
                }}
              >
                <input {...getInputProps()} />
                <Group position="center" spacing={6} sx={{ color: 'var(--gs-muted)' }}>
                  <IoCloudUploadOutline size={15} />
                  <AppText size="xs" color="dimmed">{uploading ? 'Uploading…' : 'Upload a cover'}</AppText>
                </Group>
              </Box>
              {coverImageUrl && (
                <AppButton variant="light" color="red" size="sm" leftIcon={<IoCloseCircle size={14} />} onClick={() => setCoverImageUrl(null)}>
                  Remove
                </AppButton>
              )}
            </Group>
          </Box>

          {/* Visibility */}
          <Box>
            <AppText size="sm" weight={600} mb={6}>Visibility</AppText>
            <SegmentedControl
              fullWidth
              value={visibility}
              onChange={(v) => setVisibility(v as CacheVisibility)}
              data={CACHE_VISIBILITY_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
            />
            <AppText size="xs" color="dimmed" mt={6}>{visibilityMeta(visibility).description}</AppText>
          </Box>

          {/* Registry link */}
          <Box>
            <AppText size="sm" weight={600} mb={6}>
              Link to a registry model <AppText span size="xs" color="dimmed">(optional)</AppText>
            </AppText>
            {selectedModel
              ? (
              <Box sx={{ padding: '10px 12px', border: '1px solid var(--gs-accent)', borderRadius: 8, background: 'var(--gs-surface)' }}>
                <Group position="apart" align="center" noWrap>
                  <Box>
                    <AppText size="sm" weight={600}>{selectedModel.make} {selectedModel.model}</AppText>
                    {selectedModel.caliber && <AppText size="xs" color="dimmed">{selectedModel.caliber}</AppText>}
                  </Box>
                  <AppButton variant="subtle" size="xs" color="red" onClick={clearModel}>Remove</AppButton>
                </Group>
              </Box>
                )
              : (
              <Box sx={{ position: 'relative' }}>
                <AppInput
                  placeholder="Search by make (e.g. Glock, Ruger)…"
                  value={modelSearch}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleModelSearchChange(e.currentTarget.value)}
                  rightSection={modelSearching ? <AppText size="xs" color="dimmed">…</AppText> : null}
                />
                {modelResults.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 200,
                      background: 'var(--gs-surface)',
                      border: '1px solid var(--gs-border)',
                      borderRadius: 8,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                      maxHeight: 240,
                      overflowY: 'auto',
                      marginTop: 4
                    }}
                  >
                    {modelResults.map((m) => (
                      <Box
                        key={m.id}
                        onClick={() => selectModel(m)}
                        sx={{ padding: '8px 12px', cursor: 'pointer', '&:hover': { background: 'var(--gs-surface2)' }, borderBottom: '1px solid var(--gs-border)' }}
                      >
                        <AppText size="sm" weight={600}>{m.make} {m.model}</AppText>
                        {m.caliber && <AppText size="xs" color="dimmed">{m.caliber}</AppText>}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
                )}
            <AppText size="xs" color="dimmed" mt={6}>Ties this cache to a model in the registry and inherits its image as a fallback cover.</AppText>
          </Box>

          <Group position="right">
            <AppButton component={Link} to="/caches" variant="default" disabled={submitting}>Cancel</AppButton>
            <AppButton color="ember" loading={submitting} onClick={() => void submit()}>Create cache</AppButton>
          </Group>
        </Stack>
      </AppCard>
    </Box>
  )
})
